import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/SEOHead';

export default function Terms() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://auralisconsulting.io';
  
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Terms of Service",
    "description": "Terms of Service for Auralis Consulting - Review our terms and conditions for using our consulting services",
    "url": `${siteUrl}/terms`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "Auralis Consulting",
      "url": siteUrl
    },
    "about": {
      "@type": "Thing",
      "name": "Terms of Service"
    },
    "datePublished": "2024-01-01",
    "dateModified": new Date().toISOString().split('T')[0]
  };

  return (
    <>
      <SEOHead
        title="Terms of Service - Auralis Consulting"
        description="Terms of Service for Auralis Consulting - Review our terms and conditions for using our business consulting, tax optimization, and IT management services."
        canonical={`${siteUrl}/terms`}
        ogType="article"
        schemaData={schemaData}
      />
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-8">
              Terms of Service
            </h1>
            <p className="text-gray-600 mb-12">
              Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <div className="space-y-8 text-gray-700 leading-relaxed">
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
                <p>
                  By accessing or using the services of Auralis Consulting, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services or access our website.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Services Description</h2>
                <p className="mb-3">Auralis Consulting provides professional business consulting services, including but not limited to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Corporate Strategy Consulting</li>
                  <li>Tax Optimization Services</li>
                  <li>International Business Setup</li>
                  <li>IT Network Management</li>
                  <li>English-Speaking Staff Provision</li>
                  <li>IT & Business Consulting</li>
                </ul>
                <p className="mt-3">
                  The specific scope of services will be defined in individual service agreements or engagement letters.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Responsibilities</h2>
                <p className="mb-3">When using our services, you agree to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide accurate and complete information</li>
                  <li>Maintain the confidentiality of any account credentials</li>
                  <li>Comply with all applicable laws and regulations</li>
                  <li>Not use our services for any unlawful or unauthorized purpose</li>
                  <li>Not interfere with or disrupt our services or servers</li>
                  <li>Not attempt to gain unauthorized access to our systems</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Intellectual Property</h2>
                <p>
                  All content, materials, and intellectual property on our website and in our services, including but not limited to text, graphics, logos, images, and software, are the property of Auralis Consulting or its licensors and are protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works without our express written permission.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Confidentiality</h2>
                <p>
                  We understand the sensitive nature of business consulting. All information shared during consultations and engagements will be treated as confidential. Both parties agree to maintain confidentiality of proprietary information disclosed during the course of our professional relationship, subject to the terms of any separate confidentiality or non-disclosure agreement.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Payment Terms</h2>
                <p className="mb-3">
                  Payment terms will be specified in individual service agreements. Generally:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Fees are due as specified in the engagement letter or invoice</li>
                  <li>Late payments may incur additional charges</li>
                  <li>We reserve the right to suspend services for non-payment</li>
                  <li>All fees are non-refundable unless otherwise specified in writing</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Limitation of Liability</h2>
                <p className="mb-3">
                  To the maximum extent permitted by law, Auralis Consulting shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or business opportunities, arising from:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Your use or inability to use our services</li>
                  <li>Any unauthorized access to or use of our servers</li>
                  <li>Any interruption or cessation of services</li>
                  <li>Any errors or omissions in content</li>
                </ul>
                <p className="mt-3">
                  Our total liability shall not exceed the amount paid by you for the specific service giving rise to the claim.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Professional Advice Disclaimer</h2>
                <p>
                  While we strive to provide accurate and up-to-date information, our services are provided for informational purposes and should not be construed as legal, tax, or financial advice. You should consult with appropriate licensed professionals for specific advice tailored to your situation.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Termination</h2>
                <p>
                  We reserve the right to terminate or suspend access to our services immediately, without prior notice or liability, for any reason, including breach of these Terms. Upon termination, your right to use our services will immediately cease.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Indemnification</h2>
                <p>
                  You agree to indemnify, defend, and hold harmless Auralis Consulting and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising from your use of our services or violation of these Terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Governing Law</h2>
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of the United Arab Emirates, without regard to its conflict of law provisions. Any disputes arising from these Terms or our services shall be subject to the exclusive jurisdiction of the courts of Ras Al Khaimah, UAE.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to Terms</h2>
                <p>
                  We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the updated Terms on our website with a new "Last Updated" date. Your continued use of our services after such changes constitutes acceptance of the modified Terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Severability</h2>
                <p>
                  If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary, and the remaining provisions will remain in full force and effect.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Entire Agreement</h2>
                <p>
                  These Terms, together with any service agreements or engagement letters, constitute the entire agreement between you and Auralis Consulting regarding the use of our services and supersede all prior agreements and understandings.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Contact Information</h2>
                <p className="mb-3">For questions about these Terms of Service, please contact us:</p>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="mb-2"><strong>Auralis Consulting</strong></p>
                  <p className="mb-2">Compass Building Al Shohada Road</p>
                  <p className="mb-2">AL Hamra Industrial Zone-FZ</p>
                  <p className="mb-2">Ras Al Khaimah, United Arab Emirates</p>
                  <p className="mb-2">Email: contact@auralisconsulting.io</p>
                  <p>Phone: +971 56 718 2619</p>
                </div>
              </section>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}
